/**
 * useSanityGallery Hook
 * 
 * React hook for managing Sanity gallery operations from the Gallery detail page.
 * Provides CRUD operations and sync functionality between local galleries and Sanity CMS.
 * 
 * @version 1.0.0
 * @author Claude Code
 */

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import type { Gallery as SupabaseGallery } from '@/lib/supabase'
import type { Gallery as FeatureGallery } from '../types';
import { SanityGalleryService, SanityGalleryDocument } from '@/lib/sanityGalleryService';

function toSupabaseGallery(gallery: FeatureGallery): SupabaseGallery {
  return {
    id: gallery.id,
    title: gallery.title,
    description: gallery.description || undefined,
    cover_image: gallery.cover_image ?? undefined,
    slug: gallery.slug ?? undefined,
    is_public: Boolean(gallery.is_public),
    job_number: gallery.job_number ?? undefined,
    review_status: gallery.review_status,
    service_id: gallery.service_id ?? undefined,
    used_for_social_media: gallery.used_for_social_media ?? undefined,
    franchise_region: (gallery.franchise_region ?? 'general') as SupabaseGallery['franchise_region'],
    created_on: gallery.created_on ?? new Date().toISOString()
  }
}

export interface SanityGalleryOperations {
  // State
  isLoading: boolean;
  sanityGallery: SanityGalleryDocument | null;
  sanityId: string | null;
  
  // Actions
  createInSanity: (gallery: FeatureGallery, coverImageUrl?: string) => Promise<string>;
  updateInSanity: (gallery: FeatureGallery) => Promise<void>;
  syncToSanity: (gallery: FeatureGallery, coverImageUrl?: string) => Promise<void>;
  publishInSanity: () => Promise<void>;
  archiveInSanity: () => Promise<void>;
  deleteFromSanity: () => Promise<void>;
  searchSanityGalleries: (searchTerm: string) => Promise<SanityGalleryDocument[]>;
  
  // Utilities
  setSanityId: (id: string | null) => void;
  clearSanityData: () => void;
}

/**
 * Hook for managing Sanity gallery operations
 */
export function useSanityGallery(localGallery?: FeatureGallery): SanityGalleryOperations {
  const [sanityGallery, setSanityGallery] = useState<SanityGalleryDocument | null>(null);
  const [sanityId, setSanityId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Create gallery in Sanity
  const createMutation = useMutation({
    mutationFn: async ({ gallery, coverImageUrl }: { gallery: FeatureGallery; coverImageUrl?: string }) => {
      return await SanityGalleryService.createGallery(toSupabaseGallery(gallery), coverImageUrl);
    },
    onSuccess: (newSanityId) => {
      setSanityId(newSanityId);
      toast({
        title: 'Gallery created in Sanity',
        message: 'The gallery has been successfully created in Sanity CMS',
        type: 'success'
      });
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['gallery', localGallery?.id] });
    },
    onError: (error) => {
      console.error('Failed to create gallery in Sanity:', error);
      toast({
        title: 'Sanity sync failed',
        message: `Failed to create gallery in Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  });

  // Update gallery in Sanity
  const updateMutation = useMutation({
    mutationFn: async (gallery: FeatureGallery) => {
      if (!sanityId) throw new Error('No Sanity ID available for update');
      return await SanityGalleryService.updateGallery(sanityId, toSupabaseGallery(gallery));
    },
    onSuccess: () => {
      toast({
        title: 'Gallery updated in Sanity',
        message: 'The gallery has been successfully updated in Sanity CMS',
        type: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['gallery', localGallery?.id] });
    },
    onError: (error) => {
      console.error('Failed to update gallery in Sanity:', error);
      toast({
        title: 'Sanity sync failed',
        message: `Failed to update gallery in Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  });

  // Publish gallery in Sanity
  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!sanityId) throw new Error('No Sanity ID available for publishing');
      return await SanityGalleryService.publishGallery(sanityId);
    },
    onSuccess: () => {
      toast({
        title: 'Gallery published in Sanity',
        message: 'The gallery is now live on the marketing website',
        type: 'success'
      });
      // Refresh the Sanity gallery data
      if (sanityId) {
        loadSanityGallery(sanityId);
      }
    },
    onError: (error) => {
      console.error('Failed to publish gallery in Sanity:', error);
      toast({
        title: 'Publishing failed',
        message: `Failed to publish gallery: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  });

  // Archive gallery in Sanity
  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!sanityId) throw new Error('No Sanity ID available for archiving');
      return await SanityGalleryService.archiveGallery(sanityId);
    },
    onSuccess: () => {
      toast({
        title: 'Gallery archived in Sanity',
        message: 'The gallery has been archived and removed from the public site',
        type: 'success'
      });
      if (sanityId) {
        loadSanityGallery(sanityId);
      }
    },
    onError: (error) => {
      console.error('Failed to archive gallery in Sanity:', error);
      toast({
        title: 'Archiving failed',
        message: `Failed to archive gallery: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  });

  // Delete gallery from Sanity
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!sanityId) throw new Error('No Sanity ID available for deletion');
      return await SanityGalleryService.deleteGallery(sanityId);
    },
    onSuccess: () => {
      setSanityId(null);
      setSanityGallery(null);
      toast({
        title: 'Gallery deleted from Sanity',
        message: 'The gallery has been permanently removed from Sanity CMS',
        type: 'success'
      });
    },
    onError: (error) => {
      console.error('Failed to delete gallery from Sanity:', error);
      toast({
        title: 'Deletion failed',
        message: `Failed to delete gallery: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  });

  // Load Sanity gallery data
  const loadSanityGallery = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const gallery = await SanityGalleryService.getGallery(id);
      setSanityGallery(gallery);
      return gallery;
    } catch (error) {
      console.error('Failed to load Sanity gallery:', error);
      toast({
        title: 'Failed to load Sanity data',
        message: 'Unable to fetch gallery information from Sanity',
        type: 'error'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Search Sanity galleries
  const searchSanityGalleries = useCallback(async (searchTerm: string): Promise<SanityGalleryDocument[]> => {
    try {
      return await SanityGalleryService.searchGalleries(searchTerm);
    } catch (error) {
      console.error('Failed to search Sanity galleries:', error);
      toast({
        title: 'Search failed',
        message: 'Unable to search galleries in Sanity',
        type: 'error'
      });
      return [];
    }
  }, [toast]);

  // Sync gallery to Sanity (create or update)
  const syncToSanity = useCallback(async (gallery: FeatureGallery, coverImageUrl?: string): Promise<void> => {
    if (sanityId) {
      // Update existing
      await updateMutation.mutateAsync(gallery);
      return
    } else {
      // Create new
      const newId = await createMutation.mutateAsync({ gallery, coverImageUrl });
      setSanityId(newId);
      return
    }
  }, [sanityId, createMutation, updateMutation]);

  // Handle Sanity ID changes
  const handleSetSanityId = useCallback((id: string | null) => {
    setSanityId(id);
    if (id) {
      loadSanityGallery(id);
    } else {
      setSanityGallery(null);
    }
  }, [loadSanityGallery]);

  // Clear Sanity data
  const clearSanityData = useCallback(() => {
    setSanityId(null);
    setSanityGallery(null);
  }, []);

  // Calculate loading state
  const combinedLoading = isLoading || 
    createMutation.isPending || 
    updateMutation.isPending || 
    publishMutation.isPending || 
    archiveMutation.isPending || 
    deleteMutation.isPending;

  return {
    // State
    isLoading: combinedLoading,
    sanityGallery,
    sanityId,
    
    // Actions
    createInSanity: (gallery: FeatureGallery, coverImageUrl?: string) => 
      createMutation.mutateAsync({ gallery, coverImageUrl }),
    updateInSanity: (gallery: FeatureGallery) => updateMutation.mutateAsync(gallery),
    syncToSanity,
    publishInSanity: () => publishMutation.mutateAsync(),
    archiveInSanity: () => archiveMutation.mutateAsync(),
    deleteFromSanity: () => deleteMutation.mutateAsync(),
    searchSanityGalleries,
    
    // Utilities
    setSanityId: handleSetSanityId,
    clearSanityData
  };
}

export default useSanityGallery;
